/**
 * ModerationCaseCardSection Component
 *
 * Reusable section wrapper for different content types in moderation cards.
 * Displays section icon, title, and optional count badge.
 */

import { Icon, IconSize } from '@soteria/design-system-ve';
import type { ReactNode, ComponentType } from 'react';

import * as styles from './ModerationCaseCard.css';

export interface ModerationCaseCardSectionProps {
  /**
   * Icon component to display in section header
   */
  icon: ComponentType;

  /**
   * Section title text
   */
  title: string;

  /**
   * Section content
   */
  children: ReactNode;

  /**
   * Whether to show this section (default: true)
   */
  show?: boolean;

  /**
   * Optional count badge to display after title (e.g., 3 for "Text (3)")
   * Only shows when count > 1
   */
  count?: number;

  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * Section component for ModerationCaseCard
 *
 * @example
 * ```tsx
 * <ModerationCaseCardSection icon={Chat} title="Text" count={3}>
 *   <p>Multiple text messages...</p>
 * </ModerationCaseCardSection>
 * ```
 */
export function ModerationCaseCardSection({ icon, title, children, show = true, count, className }: ModerationCaseCardSectionProps) {
  // Don't render if show is false
  if (!show) {
    return null;
  }

  const combinedClassName = [styles.sectionContainer, className].filter(Boolean).join(' ');

  return (
    <div className={combinedClassName}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>
          <Icon icon={icon} iconSize={IconSize.XSmall} />
          <span>{title}</span>
          {count && count > 1 && <span className={styles.sectionCount}>({count})</span>}
        </div>
      </div>
      <div className={styles.sectionContent}>{children}</div>
    </div>
  );
}
